import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import type { ColDef, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { getPartyBadgeColor, getPartyAbbreviation } from '../../utils/partyUtils';
import { getBillLinkClasses, getLegislatorLinkClasses } from '../../utils/linkStyles';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import { usePhone } from '../../hooks/useMediaQuery';
import DataGrid from '../shared/DataGrid';
import { reportGridLightTheme, reportGridDarkTheme } from '../shared/ag-grid-theme';
import VoteColumnLegend from './VoteColumnLegend';
import {
  VOTE_COLUMNS,
  VotesMatrixBill,
  VotesMatrixLegislator,
  VotesMatrixLegislatorVote,
  countVotesByColumn,
  filterMatrixData,
  getAlignmentColor,
  getPositionBadgeClass,
  getVoteByType,
  getVoteColorClass,
  mapBillWorkspaces,
  getBillTrackingPosition,
} from './votesMatrixUtils';

export interface VotesMatrixTableProps {
  billBreakdown: VotesMatrixBill[];
  legislatorBreakdown: VotesMatrixLegislator[];
  isOrgMode?: boolean;
  hideNoVotes?: boolean;
  onHideNoVotesChange?: (value: boolean) => void;
  isPublicView?: boolean;
  workspaceId?: string;
  getBillUrl?: (billNumber: string) => string;
  getLegislatorUrl?: (legislatorId: number | string) => string;
}

type FooterKey = 'Y' | 'N' | 'E' | 'align';

interface FooterRow {
  id: string;
  __footer: FooterKey;
  label: string;
  isFirst?: boolean;
}

interface MatrixRow extends VotesMatrixBill {
  id: string;
}

/** Shared grid context — gives every cell/header renderer access to the legislators and totals scope. */
interface MatrixGridContext {
  legsById: Map<number, VotesMatrixLegislator>;
  matrixLegs: VotesMatrixLegislator[];
  allBillIds: Set<number>;
  isOrgMode: boolean;
  isPublicView: boolean;
  workspaceId: string;
  totalsLabel: string;
  getBillUrl?: (billNumber: string) => string;
  getLegislatorUrl?: (legislatorId: number | string) => string;
}

const isFooterRow = (data: unknown): data is FooterRow =>
  !!data && typeof data === 'object' && '__footer' in (data as Record<string, unknown>);

// =====================
// CELL / HEADER RENDERERS
// =====================

/** Pinned-left Bill / Title cell (and footer labels). */
const BillCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const ctx = props.context as MatrixGridContext;

  if (isFooterRow(props.data)) {
    return (
      <div className="leading-tight">
        <span className="text-sm font-semibold text-text-primary dark:text-text-dark-primary normal-case">
          {props.data.label}
        </span>
        {props.data.isFirst && (
          <div className="text-xs font-normal text-text-tertiary dark:text-text-dark-tertiary mt-0.5 leading-tight">
            {ctx.totalsLabel}
          </div>
        )}
      </div>
    );
  }

  const bill = props.data as MatrixRow;
  const billLink =
    ctx.isPublicView && ctx.getBillUrl ? (
      <a
        href={ctx.getBillUrl(bill.billNumber)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary-600 dark:text-primary-400 hover:underline font-medium text-sm normal-case"
      >
        {bill.billNumber || '—'}
      </a>
    ) : (
      <Link
        to={`/legislation/${bill.billId}?workspace=${ctx.workspaceId}`}
        className={`${getBillLinkClasses()} text-sm normal-case`}
      >
        {bill.billNumber || '—'}
      </Link>
    );

  return (
    <div className="leading-tight py-1">
      <div className="text-sm font-medium">{billLink}</div>
      <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-0.5 normal-case whitespace-normal break-words">
        {bill.billTitle}
      </div>
    </div>
  );
};

/** Pinned-left Position (workspace) / Workspace Positions (org) cell. */
const PositionCellRenderer: React.FC<ICellRendererParams> = (props) => {
  if (isFooterRow(props.data)) return null;
  const ctx = props.context as MatrixGridContext;
  const bill = props.data as MatrixRow;

  if (ctx.isOrgMode) {
    return <WorkspaceBadgeList workspaces={mapBillWorkspaces(bill)} />;
  }

  const position = getBillTrackingPosition(bill);
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${getPositionBadgeClass(position)}`}>
      {position}
    </span>
  );
};

const renderVoteGroup = (votes: VotesMatrixLegislatorVote[]) => (
  <div className="text-sm font-mono font-medium">
    {VOTE_COLUMNS.map((col, idx) => {
      const v = getVoteByType(votes, col.type);
      return (
        <span key={col.type} title={col.title} className={getVoteColorClass(v)}>
          {v}
          {idx < VOTE_COLUMNS.length - 1 ? '/' : ''}
        </span>
      );
    })}
  </div>
);

/** Per-legislator vote cell (and footer counts / alignment). */
const LegislatorVoteCellRenderer: React.FC<ICellRendererParams & { legislatorId: number }> = (props) => {
  const ctx = props.context as MatrixGridContext;
  const legislator = ctx.legsById.get(props.legislatorId);
  if (!legislator) return null;

  if (isFooterRow(props.data)) {
    const key = props.data.__footer;
    if (key === 'align') {
      const pct = legislator.alignmentPercentage;
      const aligned = legislator.alignedVotes ?? 0;
      const third = legislator.thirdReadingVotes ?? 0;
      if (pct === null || pct === undefined || third <= 0) {
        return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>;
      }
      return (
        <div className="leading-none">
          <div className={`text-sm font-bold ${getAlignmentColor(pct)}`}>{pct}%</div>
          <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-0.5">
            {aligned}/{third}
          </div>
        </div>
      );
    }
    const counts = countVotesByColumn(legislator.votes || [], ctx.allBillIds, key);
    return (
      <span className="font-mono text-sm font-bold text-text-primary dark:text-text-dark-primary">
        {counts.join('/')}
      </span>
    );
  }

  const bill = props.data as MatrixRow;
  const votes = (legislator.votes || []).filter((v) => v.billId === bill.billId);
  return renderVoteGroup(votes);
};

/** Custom legislator column header: name link + party badge + chamber + priority dot. */
const LegislatorHeader: React.FC<IHeaderParams & { legislator: VotesMatrixLegislator }> = (props) => {
  const legislator = props.legislator;
  const ctx = props.context as MatrixGridContext;

  const link =
    ctx.isPublicView && ctx.getLegislatorUrl ? (
      <a
        href={ctx.getLegislatorUrl(legislator.legislatorId)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-white hover:underline font-bold text-xs normal-case tracking-normal"
      >
        {legislator.legislatorName}
      </a>
    ) : (
      <Link
        to={`/legislators/${legislator.legislatorId}/votes${ctx.workspaceId ? `?workspace=${ctx.workspaceId}` : ''}`}
        className={`${getLegislatorLinkClasses(legislator.party, { bold: true })} text-xs normal-case tracking-normal text-white hover:underline`}
      >
        {legislator.legislatorName}
      </Link>
    );

  return (
    <div className="flex flex-col items-center justify-center gap-1 py-1 w-full leading-tight">
      <div className="text-center whitespace-normal break-words">{link}</div>
      <div className="flex items-center justify-center gap-1.5 flex-wrap">
        <span className={`px-1 py-0.5 text-xs rounded ${getPartyBadgeColor(legislator.party)}`}>
          {getPartyAbbreviation(legislator.party) || 'I'}
        </span>
        <span className="text-xs text-white/80 normal-case tracking-normal">
          {legislator.chamber?.charAt(0) || '?'}
        </span>
        {legislator.isPriority && (
          <span className="w-2 h-2 bg-warning-400 rounded-full" title="Priority" />
        )}
      </div>
    </div>
  );
};

// =====================
// MAIN COMPONENT
// =====================

const VotesMatrixTable: React.FC<VotesMatrixTableProps> = ({
  billBreakdown,
  legislatorBreakdown,
  isOrgMode = false,
  hideNoVotes = false,
  onHideNoVotesChange,
  isPublicView = false,
  workspaceId = '',
  getBillUrl,
  getLegislatorUrl,
}) => {
  const isPhone = usePhone();

  const { filteredBills, matrixLegs, hiddenBills, hiddenLegs } = useMemo(
    () => filterMatrixData(billBreakdown, legislatorBreakdown, hideNoVotes),
    [billBreakdown, legislatorBreakdown, hideNoVotes]
  );

  const rowData = useMemo<MatrixRow[]>(
    () => filteredBills.map((b) => ({ ...b, id: String(b.billId) })),
    [filteredBills]
  );

  const legsById = useMemo(() => {
    const map = new Map<number, VotesMatrixLegislator>();
    matrixLegs.forEach((l) => map.set(l.legislatorId, l));
    return map;
  }, [matrixLegs]);

  const allBillIds = useMemo(() => new Set(filteredBills.map((b) => b.billId)), [filteredBills]);

  const totalsLabel = `Totals (all ${filteredBills.length} bill${filteredBills.length !== 1 ? 's' : ''})`;

  const gridContext = useMemo<MatrixGridContext>(
    () => ({
      legsById,
      matrixLegs,
      allBillIds,
      isOrgMode,
      isPublicView,
      workspaceId,
      totalsLabel,
      getBillUrl,
      getLegislatorUrl,
    }),
    [legsById, matrixLegs, allBillIds, isOrgMode, isPublicView, workspaceId, totalsLabel, getBillUrl, getLegislatorUrl]
  );

  const columnDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [
      {
        colId: 'bill',
        headerName: 'Bill / Title',
        pinned: 'left',
        width: 240,
        minWidth: 200,
        sortable: false,
        suppressMovable: true,
        autoHeight: true,
        wrapText: true,
        headerClass: 'ag-data-grid-header-uppercase',
        cellClass: 'votes-matrix-cell-tall',
        cellRenderer: BillCellRenderer,
      },
      {
        colId: 'position',
        headerName: isOrgMode ? 'Workspace Positions' : 'Position',
        pinned: 'left',
        width: isOrgMode ? 220 : 130,
        minWidth: 110,
        sortable: false,
        suppressMovable: true,
        autoHeight: true,
        wrapText: true,
        headerClass: 'ag-data-grid-header-uppercase',
        cellClass: 'votes-matrix-cell-tall',
        cellRenderer: PositionCellRenderer,
      },
    ];

    matrixLegs.forEach((legislator) => {
      cols.push({
        colId: String(legislator.legislatorId),
        headerComponent: LegislatorHeader,
        headerComponentParams: { legislator },
        width: 150,
        minWidth: 120,
        sortable: false,
        resizable: false,
        headerClass: 'ag-header-cell-center',
        cellClass: 'ag-cell-center',
        cellRenderer: LegislatorVoteCellRenderer,
        cellRendererParams: { legislatorId: legislator.legislatorId },
      });
    });

    return cols;
  }, [matrixLegs, isOrgMode]);

  const pinnedBottomRowData = useMemo<FooterRow[]>(
    () => [
      { id: '__footer_Y', __footer: 'Y', label: 'Total Yes', isFirst: true },
      { id: '__footer_N', __footer: 'N', label: 'Total No' },
      { id: '__footer_E', __footer: 'E', label: 'Total Excused/Absent' },
      { id: '__footer_align', __footer: 'align', label: 'Alignment % (3rd Reading vs Leader)' },
    ],
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: false,
      resizable: true,
      cellClass: 'ag-data-grid-cell',
    }),
    []
  );

  const getRowId = useMemo(() => (params: { data: MatrixRow }) => params.data.id, []);
  // Body rows auto-size to their tallest cell (workspace positions / wrapped titles).
  // Pinned footer rows can't auto-height, so give them a fixed, compact height.
  const getRowHeight = useMemo(
    () => (params: { node?: { rowPinned?: string | null } }) =>
      params?.node?.rowPinned ? 40 : undefined,
    []
  );

  if (isPhone) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-secondary px-4 py-4 text-sm text-text-secondary dark:text-text-dark-secondary">
        <AlertCircle className="w-5 h-5 text-warning-500 flex-shrink-0" />
        <span>
          The vote matrix is best viewed on a tablet or desktop. Rotate your device or open on a wider
          screen for the best experience.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <VoteColumnLegend />

      {onHideNoVotesChange && (
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-text-secondary dark:text-text-dark-secondary">
            <input
              type="checkbox"
              checked={hideNoVotes}
              onChange={(e) => onHideNoVotesChange(e.target.checked)}
              className="rounded border-border-light dark:border-border-dark"
            />
            Hide bills / legislators with no votes
          </label>
          <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
            {hideNoVotes && (hiddenBills > 0 || hiddenLegs > 0) && (
              <>
                Hiding {hiddenBills} bill{hiddenBills !== 1 ? 's' : ''} and {hiddenLegs} legislator
                {hiddenLegs !== 1 ? 's' : ''} with no votes ·{' '}
              </>
            )}
            {filteredBills.length} bill{filteredBills.length !== 1 ? 's' : ''} · {matrixLegs.length}{' '}
            legislator{matrixLegs.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {matrixLegs.length > 0 && filteredBills.length > 0 ? (
        <DataGrid<MatrixRow>
          data={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridContext={gridContext}
          getRowId={getRowId}
          getRowHeight={getRowHeight}
          pinnedBottomRowData={pinnedBottomRowData}
          lightTheme={reportGridLightTheme}
          darkTheme={reportGridDarkTheme}
          headerHeight={76}
          wrapperClassName="votes-matrix-grid"
        />
      ) : (
        <div className="text-center py-8 text-text-tertiary dark:text-text-dark-tertiary">
          {hideNoVotes
            ? 'No bills with final-reading votes found. Uncheck "Hide bills / legislators with no votes" to see all tracked items.'
            : 'No vote data available for matrix view'}
        </div>
      )}
    </div>
  );
};

export default VotesMatrixTable;
